/**
 * Release-notes renderer for the customer-facing releases SPA.
 *
 * Parses the markdown-ish bullet format CI bakes into every release
 * manifest (`- subject (abc1234)` per line). Renders cleanly for end
 * users — no CI pipeline links (internal infra, not customer-facing),
 * no GitHub hash links (keeps the surface minimal and avoids dead
 * clicks for anyone not signed into the repo). The short SHA is shown
 * as a dim chip next to each entry so operators can grep if needed.
 */

const BULLET_LINE = /^- (.+?)(?:\s+\(([0-9a-f]{7,40})\))?\s*$/i;

interface ReleaseNotesBodyProps {
  notes: string | null | undefined;
  className?: string;
}

export function ReleaseNotesBody({ notes, className = "" }: ReleaseNotesBodyProps) {
  const trimmed = (notes ?? "").trim();
  if (!trimmed) {
    return (
      <p className={`text-sm italic text-zinc-500 ${className}`}>
        No changes recorded for this release.
      </p>
    );
  }

  const lines = trimmed.split("\n");
  const bullets: Array<{ text: string; hash?: string }> = [];
  const stray: string[] = [];
  for (const raw of lines) {
    const match = raw.match(BULLET_LINE);
    if (match && match[1]) {
      bullets.push({ text: match[1], hash: match[2] });
    } else if (raw.trim()) {
      stray.push(raw);
    }
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {stray.length > 0 && (
        <div className="space-y-1">
          {stray.map((p, i) => (
            <p key={i} className="text-sm text-zinc-400 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      )}
      {bullets.length > 0 && (
        <ul className="space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
              <span className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
              <span className="flex-1 text-zinc-300">
                {b.text}
                {b.hash && (
                  <span
                    className="ml-2 inline-block font-mono text-[10px] text-zinc-500"
                    title={b.hash}
                  >
                    {b.hash.slice(0, 7)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
